import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle2, Mail, RotateCw } from "lucide-react";

export default function InviteLinkDialog({ open, onOpenChange, coach }) {
  const [link, setLink] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (!open || !coach) return;
    setLink("");
    setError("");
    setCopied(false);
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/coaches/${coach.user_id}/invite`);
        setLink(data.invite_link);
        setExpiresAt(data.invite_expires_at);
      } catch (e) {
        if (e.response?.status === 404) {
          setError("Kein aktiver Einladungslink mehr. Erstelle einen neuen unten.");
        } else {
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, coach]);

  const regenerate = async () => {
    if (!coach) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/coaches/${coach.user_id}/reinvite`);
      setLink(data.invite_link);
      setExpiresAt(data.invite_expires_at);
      toast.success("Neuer Einladungslink erstellt");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link kopiert");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Kopieren fehlgeschlagen — manuell aus dem Feld kopieren");
    }
  };

  const formattedExpiry = expiresAt
    ? new Date(expiresAt).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="invite-link-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <Mail size={12} strokeWidth={1.5} />
            Einladungslink
          </div>
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
            {coach?.name}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-slate-500 mt-1">
            {coach?.email}
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-5">
          {loading && <div className="text-sm text-slate-500">Lädt…</div>}

          {!loading && link && (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                  Aktiver Einladungslink
                </label>
                <div
                  className="border border-slate-200 bg-slate-50 p-3 text-xs font-mono text-slate-700 break-all"
                  data-testid="invite-link-output"
                >
                  {link}
                </div>
                {formattedExpiry && (
                  <p className="text-xs text-slate-500 mt-2">Gültig bis: {formattedExpiry}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="invite-link-copy"
                  onClick={copy}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-900 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors"
                >
                  {copied ? <CheckCircle2 size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.5} />}
                  {copied ? "Kopiert!" : "Link kopieren"}
                </button>
                <button
                  data-testid="invite-link-regenerate"
                  onClick={regenerate}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-100 transition-colors"
                  title="Neuen Link erzeugen (der alte wird ungültig)"
                >
                  <RotateCw size={12} strokeWidth={1.5} />
                  Neuen Link erzeugen
                </button>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Schicke diesen Link an <span className="font-mono">{coach?.email}</span>. Die Person legt
                selbst ein Passwort fest oder loggt sich direkt mit Google ein.
              </p>
            </>
          )}

          {!loading && !link && error && (
            <>
              <div className="text-sm text-slate-700 border-l-2 border-amber-600 pl-3 py-1">{error}</div>
              <button
                data-testid="invite-link-regenerate"
                onClick={regenerate}
                className="inline-flex items-center gap-2 px-4 py-2 border border-slate-900 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors"
              >
                <RotateCw size={12} strokeWidth={1.5} />
                Neuen Einladungslink erstellen
              </button>
            </>
          )}
        </div>

        <DialogFooter className="px-8 py-5 border-t border-slate-100">
          <button
            data-testid="invite-link-close"
            onClick={() => onOpenChange(false)}
            className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Schließen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
